/**
 * VaultDocuments · reusable upload + list widget for one category.
 *
 * Renders:
 *   - a compact drop-zone (file input + label + subcategory + evidence status)
 *   - a list of already-uploaded documents with status badges + actions
 *
 * Meant to be embedded inside the dossier questionnaire (Formation,
 * Expérience, Fiscalité…) so a client can attach their justificatifs where
 * they naturally think about them, not only in a separate "Vault" page.
 *
 * Props:
 *   dossierId  — string, required
 *   category   — one of formation|experience|fiscalite|identite|revenus|
 *                 actifs|passifs|assurances|projet
 *   linkedSections — optional string[] (e.g. ["parcours.formation"]) to help
 *                     downstream engines know which data-point the doc supports
 *   subcategoryOptions — optional array of {value,label} presets shown in a
 *                        dropdown to keep taxonomy tidy per section
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Upload, FileText, Trash2, ShieldCheck, Loader2,
  CheckCircle2, AlertCircle, FileWarning, Clock, Download, Wand2,
} from "lucide-react";
import ExtractedFieldsEditor from "@/components/ExtractedFieldsEditor";


const STATUS_COLORS = {
  declared:      { bg: "#F5F1E8", fg: "#7A5B14", label: "Déclaré" },
  documented:    { bg: "#E0EAF7", fg: "#0B2545", label: "Documenté" },
  analyzed:      { bg: "#EDE6FA", fg: "#5B21B6", label: "Analysé IA" },
  confirmed:     { bg: "#DCF4E4", fg: "#065F46", label: "Confirmé" },
  verified:      { bg: "#059669", fg: "#FFFFFF", label: "Vérifié" },
  contradictory: { bg: "#FEE2E2", fg: "#991B1B", label: "Contradictoire" },
  expired:       { bg: "#FFEDD5", fg: "#9A3412", label: "Expiré" },
  illegible:     { bg: "#F3F4F6", fg: "#374151", label: "Illisible" },
  incomplete:    { bg: "#FEF3C7", fg: "#92400E", label: "Incomplet" },
  na:            { bg: "#F3F4F6", fg: "#6B7280", label: "N/A" },
};


export default function VaultDocuments({
  dossierId,
  category,
  linkedSections = [],
  subcategoryOptions = [],
  title,
  helpText,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(null);
  const [analyzing, setAnalyzing] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    if (!dossierId) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get(
        `/vault/documents?dossier_id=${encodeURIComponent(dossierId)}&category=${category}`
      );
      setItems(data.items || []);
    } catch (err) {
      // Non-fatal — some dossiers may not exist yet
      setItems([]);
    } finally { setLoading(false); }
  }, [dossierId, category]);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async (evt) => {
    const file = evt.target.files?.[0];
    if (!file) return;
    const label = window.prompt(
      "Nom court pour ce document (visible dans le dossier) :",
      file.name.replace(/\.[^.]+$/, "")
    );
    if (label === null) { evt.target.value = ""; return; }

    const form = new FormData();
    form.append("dossier_id", dossierId);
    form.append("category", category);
    form.append("label", label || file.name);
    if (subcategoryOptions.length > 0) form.append("subcategory", subcategoryOptions[0].value);
    if (linkedSections.length > 0) form.append("linked_sections", linkedSections.join(","));
    form.append("evidence_status", "documented");
    form.append("file", file);

    setUploading(true);
    try {
      await api.post("/vault/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Document « ${label || file.name} » importé.`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import impossible.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async (docId, label) => {
    if (!window.confirm(`Retirer « ${label} » du coffre ? Cette action est irréversible.`)) return;
    try {
      await api.delete(`/vault/documents/${docId}`);
      toast.success("Document retiré.");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Suppression impossible.");
    }
  };

  const setStatus = async (docId, newStatus) => {
    try {
      await api.patch(`/vault/documents/${docId}`, { evidence_status: newStatus });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Modification impossible.");
    }
  };

  const download = async (docId, filename) => {
    try {
      const resp = await api.get(`/vault/documents/${docId}/download`, { responseType: "blob" });
      const blob = new Blob([resp.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Téléchargement impossible.");
    }
  };

  const analyzeOne = async (docId) => {
    setAnalyzing(docId);
    try {
      const { data } = await api.post(`/vault/documents/${docId}/analyze`);
      toast.success(`Analyse IA — ${data.confidence}% de confiance.`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Analyse impossible.");
    } finally { setAnalyzing(null); }
  };

  return (
    <div className="rounded-xl border border-[#E4E1D8] bg-[#FBF7EE]/40 p-4"
         data-testid={`vault-doc-block-${category}`}>
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-[#7A5B14]" strokeWidth={2} />
        <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#7A5B14]">
          {title || `Preuves · ${category}`}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-[10px] font-mono text-[#5B5B62]">
            {items.length} pièce{items.length > 1 ? "s" : ""}
          </span>
          <label
            className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[11px] cursor-pointer transition
                        ${uploading ? "opacity-60 cursor-wait" : "hover:bg-white"}
                        border-[#D5CDB8] text-[#0B2545]`}
            data-testid={`vault-upload-btn-${category}`}
          >
            {uploading
              ? <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2} />
              : <Upload className="h-3 w-3" strokeWidth={2} />}
            Téléverser
            <input ref={fileRef} type="file" onChange={handleUpload} disabled={uploading}
                   className="hidden"
                   data-testid={`vault-file-input-${category}`}
                   accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.jpg,.jpeg,.png,.webp,.heic,.zip" />
          </label>
        </div>
      </div>

      {helpText && (
        <div className="mt-2 text-[11px] text-[#5B5B62] leading-relaxed">{helpText}</div>
      )}

      {loading && (
        <div className="mt-3 flex items-center gap-2 text-xs text-[#5B5B62]">
          <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2} /> Chargement…
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className="mt-3 rounded-md border border-dashed border-[#D5CDB8] px-3 py-4 text-[11px] text-[#5B5B62] text-center">
          Aucune pièce jointe pour le moment. Un document accroît la fiabilité du diagnostic.
        </div>
      )}

      {!loading && items.length > 0 && (
        <ul className="mt-3 space-y-1.5" data-testid={`vault-doc-list-${category}`}>
          {(expanded ? items : items.slice(0, 4)).map((doc) => {
            const st = STATUS_COLORS[doc.evidence_status] || STATUS_COLORS.documented;
            return (
              <li key={doc.id}
                  id={`vault-doc-${doc.id}`}
                  className="flex items-center gap-2.5 rounded-md bg-white border border-[#EDE7D6] px-3 py-2"
                  data-testid={`vault-doc-${doc.id}`}>
                <FileText className="h-3.5 w-3.5 text-[#0B2545] flex-shrink-0" strokeWidth={2} />
                <div className="min-w-0 flex-1">
                  <div className="text-xs text-[#1F2937] truncate">{doc.label}</div>
                  <div className="text-[10px] font-mono text-[#8B8B8F] truncate">
                    {doc.filename} · {(doc.size_bytes / 1024).toFixed(0)} Ko
                    {doc.expiry_date && ` · expire le ${doc.expiry_date}`}
                    {doc.extraction_confidence != null && ` · IA ${doc.extraction_confidence}%`}
                  </div>
                </div>
                <select
                  value={doc.evidence_status}
                  onChange={(e) => setStatus(doc.id, e.target.value)}
                  data-testid={`vault-doc-status-${doc.id}`}
                  className="rounded px-1.5 py-0.5 text-[10px] font-mono border cursor-pointer"
                  style={{ backgroundColor: st.bg, color: st.fg, borderColor: st.bg }}
                >
                  {Object.entries(STATUS_COLORS).map(([k, v]) => (
                    <option key={k} value={k}>{v.label}</option>
                  ))}
                </select>
                {(!doc.attributes?.extracted || Object.keys(doc.attributes.extracted).length === 0) ? (
                  <button onClick={() => analyzeOne(doc.id)}
                          disabled={analyzing === doc.id}
                          data-testid={`vault-doc-analyze-${doc.id}`}
                          title="Analyser avec Claude Vision"
                          className="text-[#5B5B62] hover:text-[#5B21B6] transition">
                    {analyzing === doc.id
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={2} />
                      : <Wand2 className="h-3.5 w-3.5" strokeWidth={2} />}
                  </button>
                ) : (
                  <button onClick={() => setEditing(doc)}
                          data-testid={`vault-doc-edit-${doc.id}`}
                          title="Corriger les champs extraits"
                          className="text-[#5B5B62] hover:text-[#7A5B14] transition text-[10px] font-mono uppercase tracking-widest">
                    Corriger
                  </button>
                )}
                <button onClick={() => download(doc.id, doc.filename)}
                        data-testid={`vault-doc-download-${doc.id}`}
                        className="text-[#5B5B62] hover:text-[#0B2545] transition">
                  <Download className="h-3.5 w-3.5" strokeWidth={2} />
                </button>
                <button onClick={() => remove(doc.id, doc.label)}
                        data-testid={`vault-doc-delete-${doc.id}`}
                        className="text-[#5B5B62] hover:text-red-700 transition">
                  <Trash2 className="h-3.5 w-3.5" strokeWidth={2} />
                </button>
              </li>
            );
          })}
          {items.length > 4 && (
            <button onClick={() => setExpanded(!expanded)}
                    className="text-[10px] font-mono uppercase tracking-widest text-[#7A5B14] hover:text-[#0B2545] transition"
                    data-testid={`vault-doc-expand-${category}`}>
              {expanded ? "— Réduire" : `+ Voir ${items.length - 4} pièce${items.length - 4 > 1 ? "s" : ""}`}
            </button>
          )}
        </ul>
      )}
      {editing && (
        <ExtractedFieldsEditor doc={editing}
                                 onClose={() => setEditing(null)}
                                 onSaved={load} />
      )}
    </div>
  );
}


/**
 * StatusIcon — resolves an evidence status to a small colored icon (used
 * outside of the widget, e.g. in overview cards).
 */
export function EvidenceStatusIcon({ status }) {
  const s = STATUS_COLORS[status] || STATUS_COLORS.documented;
  const Icon = {
    verified: ShieldCheck,
    confirmed: CheckCircle2,
    contradictory: AlertCircle,
    expired: Clock,
    illegible: FileWarning,
    incomplete: FileWarning,
  }[status] || FileText;
  return <Icon className="h-3.5 w-3.5" style={{ color: s.fg }} strokeWidth={2} />;
}
